<template>
  <div class="import-results">
    <!-- Processing State -->
    <div v-if="isProcessing" class="processing-state">
      <h3 class="mb-4">Import in Progress</h3>

      <div class="progress-container">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progress.percentage + '%' }"></div>
        </div>
        <div class="progress-text">
          {{ progress.processed }} of {{ progress.total }} rows processed ({{ progress.percentage }}%)
        </div>
      </div>

      <div class="status-message">
        <div class="spinner"></div>
        <span>Processing your driving sessions...</span>
      </div>

      <p class="text-body-2 mt-4">
        Please wait while we import your driving sessions. This may take a few minutes for large files.
        You can safely leave this page - the import will continue in the background.
      </p>
    </div>

    <!-- Failed State -->
    <div v-else-if="isFailed" class="failed-state">
      <h3 class="mb-4">Import Failed</h3>

      <div class="alert inline error">
        <span class="error-icon">!</span>
        <span>{{ failureReason || 'An unexpected error occurred during import.' }}</span>
      </div>

      <p class="text-body-2 mt-4">
        Please check your CSV file and try again. If the problem persists, contact support.
      </p>
    </div>

    <!-- Completed State -->
    <div v-else-if="isCompleted">
      <h3 class="mb-4">Import Results</h3>

      <div class="alert inline success" v-if="importedCount > 0">
        Successfully imported {{ importedCount }} driving sessions!
      </div>

      <div class="stats-row" v-if="importedCount !== undefined || skippedCount !== undefined">
        <div class="stat-card">
          <div class="stat-value">{{ importedCount || 0 }}</div>
          <div class="stat-label">Drives Imported</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ skippedCount || 0 }}</div>
          <div class="stat-label">Skipped</div>
        </div>
      </div>

      <div class="errors-section" v-if="errorCount > 0">
        <h4 class="errors-heading">Errors ({{ errorCount }})</h4>
        <div class="errors-list-container">
          <ul class="errors-list">
            <li v-for="(error, index) in errors" :key="index" class="error-item">
              <span class="error-icon">!</span>
              <span class="error-message">{{ formatError(error) }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="mt-6 text-center">
        <p class="text-body-2">
          The import process is complete. You can now view these driving sessions in your calendar.
        </p>
      </div>
    </div>

    <!-- Pending State (waiting to start) -->
    <div v-else class="pending-state">
      <h3 class="mb-4">Import Queued</h3>
      <div class="status-message">
        <div class="spinner"></div>
        <span>Waiting for import to start...</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    results: {
      type: Object,
      default: () => ({})
    },
    status: {
      type: String,
      default: null
    },
    progress: {
      type: Object,
      default: () => ({
        total: 0,
        processed: 0,
        percentage: 0
      })
    },
    failureReason: {
      type: String,
      default: null
    }
  },

  computed: {
    isProcessing() {
      return this.status === 'processing' || this.status === 'pending';
    },

    isCompleted() {
      return this.status === 'completed' || (!this.status && this.results && Object.keys(this.results).length > 0);
    },

    isFailed() {
      return this.status === 'failed';
    },

    errors() {
      return this.results?.errors || [];
    },

    errorCount() {
      return this.errors.length;
    },

    importedCount() {
      return this.results?.imported ?? this.results?.imported_count ?? 0;
    },

    skippedCount() {
      return this.results?.skipped ?? this.results?.skipped_count ?? 0;
    }
  },

  methods: {
    formatError(error) {
      if (typeof error === 'string') {
        return error;
      }
      if (error.row !== undefined && error.message) {
        return `Row ${error.row}: ${error.message}`;
      }
      if (error.message) {
        return error.message;
      }
      return JSON.stringify(error);
    }
  }
}
</script>

<style scoped>
.import-results {
  padding: 1rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mt-4 {
  margin-top: 1rem;
}

.mt-6 {
  margin-top: 1.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.py-4 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.text-center {
  text-align: center;
}

/* Progress Bar Styles */
.progress-container {
  margin: 1.5rem 0;
}

.progress-bar {
  height: 24px;
  background-color: #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #1976d2;
  border-radius: 12px;
  transition: width 0.3s ease;
}

.progress-text {
  margin-top: 0.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: #666;
}

/* Status Message */
.status-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-top: 1rem;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Alerts */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.alert.success {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.alert.error {
  background-color: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
}

.stats-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.stat-card {
  flex: 1;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 16px;
  text-align: center;
}

.stat-value {
  font-size: 2rem;
  font-weight: 600;
  color: #1976d2;
}

.stat-label {
  font-size: 0.875rem;
  color: #666;
  margin-top: 4px;
}

.errors-section {
  margin-top: 1.5rem;
  background: #fff;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  overflow: hidden;
}

.errors-heading {
  margin: 0;
  padding: 0.75rem 1rem;
  background-color: #ffebee;
  color: #c62828;
  font-size: 1.25rem;
  font-weight: 600;
  border-bottom: 1px solid #ffcdd2;
}

.errors-list-container {
  max-height: 300px;
  overflow-y: auto;
}

.errors-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.error-item {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #ffebee;
  background: #fff;
}

.error-item:last-child {
  border-bottom: none;
}

.error-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background-color: #c62828;
  color: #fff;
  font-weight: bold;
  font-size: 14px;
  margin-right: 0.75rem;
  margin-top: 2px;
}

.error-message {
  color: #333;
  font-size: 1rem;
  line-height: 1.5;
}

.text-body-2 {
  font-size: 0.875rem;
  color: #666;
}
</style>
